import {
  EnumRichiestaDTOStato,
  GetFrontRichiesteStatoDelega,
  GetFrontRichiesteStatoTipoDomanda,
} from '@framework/api-client';
import { SelectInput } from '@framework/form';
import { t } from '@framework/i18n';
import {
  SearchableField,
  SearchableFieldGroup,
  SearchableFieldType,
} from '@framework/ui';
import { capitalize } from 'lodash';
import { useMemo } from 'react';
import { InquiryTypeSelectInput } from '../../inquiries/components/InquiryTypeSelectInput';
import { FrontCategoryAsyncSelectInput } from './FrontCategoryAsyncSelectInput';

/** Stati ammissibili per il filtro stato tipo domadna (stati delle domande utilizzabili per la pubblicazione) */
const INQUIRY_STATUSES = [
  GetFrontRichiesteStatoTipoDomanda.PUBBLICATA,
  GetFrontRichiesteStatoTipoDomanda.APERTA,
  GetFrontRichiesteStatoTipoDomanda.IN_CORSO,
  GetFrontRichiesteStatoTipoDomanda.COLLAUDO,
  GetFrontRichiesteStatoTipoDomanda.CHIUSA,
];

/** Stati ammissibili per il filtro stato richiesta */
export const REQUEST_STATUSES = [
  // Lo stato "Creata" non deve essere visualizzabile
  // { label: 'Creata', value: EnumRichiestaDTOStato.CREATA },
  {
    label: t('submission-searchable-fields.bozza', 'Bozza'),
    value: EnumRichiestaDTOStato.BOZZA,
  },
  {
    label: t('submission-searchable-fields.integrata', 'Integrata'),
    value: EnumRichiestaDTOStato.INTEGRATA,
  },
  {
    label: t('submission-searchable-fields.inviata', 'Inviata'),
    value: EnumRichiestaDTOStato.INVIATA,
  },
  {
    label: t('submission-searchable-fields.ricevuta', 'Ricevuta'),
    value: EnumRichiestaDTOStato.RICEVUTA,
  },
  {
    label: t('submission-searchable-fields.conclusa', 'Conclusa'),
    value: EnumRichiestaDTOStato.CONCLUSA,
  },
];

const staticSubmissionSearchableFields: SearchableField[] = [
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.oggetto', 'Oggetto'),
    name: 'oggetto',
    placeholder: t('submission-searchable-fields.oggetto', 'Oggetto'),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-columns.numero-protocollo', 'Numero Protocollo'),
    name: 'numeroProtocollo',
    placeholder: t('submission-columns.numero-protocollo', 'Numero Protocollo'),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.stato-richiesta', 'Stato Richiesta'),
    name: 'statoRichiesta',
    group: SearchableFieldGroup.Filters,
    input: (
      <SelectInput
        name="statoRichiesta"
        size="middle"
        mode="multiple"
        allowClear
        placeholder={t(
          'submission-searchable-fields.stato-richiesta',
          'Stato Richiesta',
        )}
        options={REQUEST_STATUSES}
      />
    ),
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.nome-titolare', 'Nome Titolare'),
    name: 'nomeTitolare',
    placeholder: t(
      'submission-searchable-fields.nome-titolare',
      'Nome Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.cognome-titolare',
      'Cognome Titolare',
    ),
    name: 'cognomeTitolare',
    placeholder: t(
      'submission-searchable-fields.cognome-titolare',
      'Cognome Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.codice-fiscale-titolare',
      'Codice Fiscale Titolare',
    ),
    name: 'codiceFiscaleTitolare',
    placeholder: t(
      'submission-searchable-fields.codice-fiscale-titolare',
      'Codice Fiscale Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.stato-delega', 'Stato Delega'),
    name: 'statoDelega',
    group: SearchableFieldGroup.Filters,
    input: (
      <SelectInput
        name="statoDelega"
        size="middle"
        allowClear
        placeholder={t(
          'submission-searchable-fields.stato-delega',
          'Stato Delega',
        )}
        options={Object.values(GetFrontRichiesteStatoDelega).map(value => ({
          value,
          label: capitalize(value),
        }))}
      />
    ),
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.categoria', 'Categoria'),
    name: 'categorie',
    group: SearchableFieldGroup.Filters,
    input: (
      <FrontCategoryAsyncSelectInput
        size="middle"
        name="categorie"
        mode="multiple"
        placeholder={t(
          'submission-searchable-fields.seleziona-le-categorie',
          'Seleziona le categorie',
        )}
      />
    ),
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.ricerca-estesa', 'Ricerca Estesa'),
    name: 'valoriRicercaEstesa',
    placeholder: t(
      'submission-searchable-fields.ricerca-estesa',
      'Ricerca Estesa',
    ),
    group: SearchableFieldGroup.Filters,
  },
  // {
  //   type: SearchableFieldType.Text,
  //   label: t('submission-searchable-fields.numero-istanza', 'Numero Istanza'),
  //   name: 'progressivo',
  //   placeholder: t(
  //     'submission-searchable-fields.numero-istanza',
  //     'Numero Istanza',
  //   ),
  //   group: SearchableFieldGroup.Filters,
  // },
  {
    type: SearchableFieldType.Number,
    label: t('submission-searchable-fields.anno-istanza', 'Anno Istanza'),
    name: 'progressivoAnno',
    placeholder: t('submission-searchable-fields.anno-istanza', 'Anno Istanza'),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolo-tipo-domanda',
      'Titolo Tipo Domanda',
    ),
    name: 'titoloTipoDomanda',
    placeholder: t(
      'submission-searchable-fields.titolo-tipo-domanda',
      'Titolo Tipo Domanda',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.tipo-domanda', 'Tipo Domanda'),
    name: 'idGruppoTipoDomanda',
    group: SearchableFieldGroup.Filters,
    input: (
      <InquiryTypeSelectInput
        name="idGruppoTipoDomanda"
        placeholder={t(
          'submission-searchable-fields.tipo-domanda',
          'Tipo Domanda',
        )}
        size="middle"
      />
    ),
  },
  {
    type: SearchableFieldType.DateRange,
    label: t('submission-searchable-fields.data-creazione', 'Data creazione'),
    name: 'dataCreazione',
    group: SearchableFieldGroup.Filters,
    from: {
      name: 'dataAvvioCompilazioneDa',
      label: t(
        'submission-searchable-fields.data-avvio-compilazione-dal',
        'Data avvio compilazione dal',
      ),
    },
    to: {
      name: 'dataAvvioCompilazioneA',
      label: t(
        'submission-searchable-fields.data-avvio-compilazione-al',
        'Data avvio compilazione al',
      ),
    },
  },
  {
    type: SearchableFieldType.DateRange,
    label: t('submission-searchable-fields.data-invio', 'Data invio'),
    name: 'dataInvio',
    group: SearchableFieldGroup.Filters,
    from: {
      name: 'dataInvioCompilazioneDa',
      label: t('submission-searchable-fields.data-invio-dal', 'Data invio dal'),
    },
    to: {
      name: 'dataInvioCompilazioneA',
      label: t('submission-searchable-fields.data-invio-al', 'Data invio al'),
    },
  },
];

export function useSubmissionSearchableFields(options: {
  extraFields?: SearchableField[];
}) {
  const searchableFields: SearchableField[] = useMemo(
    () =>
      [
        ...staticSubmissionSearchableFields,
        ...(options.extraFields ?? []),
      ].sort((a, b) => (a.label > b.label ? 1 : -1)),
    [options.extraFields],
  );

  return searchableFields;
}
